<template>
  <div class="chat-widget__message-inner">
    <div class="chat-widget__message-header">
      <span class="chat-widget__message-sender">
        <Avatar v-if="message.assistant" size="large" :image="message.assistant.icon" shape="circle" />
        <Avatar :icon="userIcon" shape="circle" v-else-if="message.type !== MESSAGE_TYPE_ASSISTANT"/>
        <Avatar :icon="assistantIcon" shape="circle" v-else/>
        <div v-if="message.assistant">
          <div class="chat-widget__message-time">{{ message.assistant.name }}</div>
        </div>
      </span>
      <span class="chat-widget__message-time">
         {{ formatDateTime(message.created_at).date }} {{ formatDateTime(message.created_at).time }}
      </span>
    </div>
    <div class="chat-widget__message-content">
      <ChatReasonMessage :reason="message.reason"
                         v-if="message.reason && [FINISH_REASON_BLACKLIST, FINISH_REASON_LENGTH, FINISH_REASON_MAX_TOKENS].includes(message.reason)"/>
      <div class="chat-widget__message-body user-message" v-if="userText">{{ userText }}</div>
      <div class="chat-widget__message-body" v-html="parsedText" v-else-if="parsedText"></div>
      <ChatMessageLinks :links="message.search?.links"/>
      <div class="chat-widget__message-gallery" v-if="images && images.length">
        <p>Нажмите на изображение, чтобы увеличить</p>
        <ChatGalleryUml galleryID="my-test-gallery" :image="images[0]" v-if="message.uml && message.uml.length"/>
        <ChatGallery galleryID="my-test-gallery" :messageId="message.id" :images="images" v-else/>
      </div>
      <div class="chat-widget__message-files" v-if="message.files && message.files.length">
        <ChatAttachment
            v-for="file in message.files"
            :key="file.id"
            :file="file"
            :message-id="message.id"
        />
      </div>
      <div class="chat-widget__music-files" v-if="message.music_files && message.music_files.length">
        <MusicAttachments :files="message.music_files"/>
      </div>
    </div>
  </div>
  <div class="chat-widget__message-footer">
    <div class="chat-widget__message-stat" v-if="message.type === MESSAGE_TYPE_ASSISTANT && message.stat?.total_tokens">
      {{ pluralForm(message.stat.total_tokens, ['токен', 'токена', 'токенов']) }}&nbsp;(<i class="pi pi-angle-up"/>{{ message.stat.prompt_tokens }}&nbsp;|&nbsp;{{  message.stat.completion_tokens }}<i class="pi pi-angle-down"/>)<span v-if="message.web_search">&nbsp;<i v-tooltip.bottom="'Контекст содержит результаты WEB-поиска'" class="pi pi-globe"/></span>
    </div>
    <div class="chat-widget__message-control">
      <Button variant="text"
              label=""
              @click="visibleEditModal = true"
              v-tooltip.bottom="'Редактировать'"
              v-if="appStore.features.editMessage && message.type === MESSAGE_TYPE_USER"
              icon="pi pi-pencil"
              iconPos="right">
      </Button>
      <Button variant="text"
              @click="handleCopy(parsedText)"
              label=""
              v-tooltip.bottom="'Копировать'"
              v-if="parsedText.trim()"
              :icon="copyIcon"
              iconPos="right">
      </Button>
      <Button variant="text"
              @click="confirmRegenerate(message.id)"
              label=""
              v-if="message.can_regenerate"
              :disabled="appStore.blockSending"
              v-tooltip.bottom="regenerateTooltip"
              icon="pi pi-sync"
              iconPos="right">
      </Button>

      <Button variant="text"
              v-if="showPopover"
              @click="toggle"
              label=""
              icon="pi pi-ellipsis-v"
              iconPos="right">
      </Button>

      <Popover ref="popover" class="chat-widget__message-popover">
        <Button severity="secondary" type="button" icon="pi pi-replay" icon-pos="left" label="Отправить еще раз"
                text
                :disabled="appStore.blockSending"
                v-tooltip.bottom="regenerateTooltip"
                v-if="showResendButton"
                @click="handleRepeatSend(message.id)"/>
        <Button severity="secondary" type="button" icon="pi pi-copy" icon-pos="left" label="Скопировать оригинальный ответ"
                v-if="showCopyOriginalMessage"
                text
                @click="handleOriginalCopy()"/>
        <Button variant="text"
                @click="confirmDelete()"
                label="Удалить"
                v-tooltip.bottom="'Удалить'"
                v-if="appStore.canDeleteMessage"
                icon="pi pi-trash"
                iconPos="left">
        </Button>

      </Popover>
    </div>
  </div>
  <EditDialog
      v-model="visibleEditModal"
      :message="message"
  />
</template>
<script setup>
import {computed, defineAsyncComponent, onMounted, ref} from 'vue';
import Button from 'primevue/button';
import Avatar from 'primevue/avatar';
import Popover from "primevue/popover";
import ChatReasonMessage from "@/modules/chat/components/message/ChatReasonMessage.vue";
import ChatGalleryUml from "@/modules/chat/components/gallery/ChatGalleryUml.vue";
import ChatGallery from "@/modules/chat/components/gallery/ChatGallery.vue";
import ChatAttachment from "@/modules/chat/components/message/ChatAttachment.vue";
import Prism from 'prismjs'
import {
  FINISH_REASON_BLACKLIST,
  FINISH_REASON_LENGTH,
  FINISH_REASON_MAX_TOKENS,
  MESSAGE_TYPE_ASSISTANT, MESSAGE_TYPE_USER,
} from "@/constants";
import {
  copyMessage,
  formatDateTime, pluralForm,
  prepareAiMessage,
} from "@/services/helper";
import {getImages, getUml} from "@/modules/chat/services/image";
import { useConfirm } from "primevue/useconfirm";
import {useToastStore} from "@/modules/chat/stores/toast";
import {useChatStore} from "@/modules/chat/stores/chatStore";
import {useAppStateStore} from "@/modules/chat/stores/appStore";
import ChatMessageLinks from "@/modules/chat/components/message/ChatMessageLinks.vue";
import {formLinks} from "@/modules/chat/services/search";
import EditDialog from "@/modules/chat/components/edit/EditDialog.vue";

const MusicAttachments = defineAsyncComponent(() =>
    import("@/modules/chat/components/message/MusicAttachments.vue")
);

const userIcon = 'pi pi-user';
const assistantIcon = 'pi pi-sparkles';
const defaultCopyIcon = 'pi pi-copy';
const successCopyIcon = 'pi pi-check';
const copyIcon = ref(defaultCopyIcon);
const props = defineProps({
  message: {
    type: Object,
    required: true
  }
});
const toastStore = useToastStore();
const appStore = useAppStateStore();
const chatStore = useChatStore();
const handleCopy = async (parsedText) => {
  let textToCopy = parsedText;

  // Добавляем ссылки к копируемому тексту, если они есть
  if (props.message.search?.links?.length) {
    const list = await formLinks(props.message.search.links);
    textToCopy += `${list}`;
  }

  await copyMessage(textToCopy);
  toastStore.showSecondary('Текст скопирован в буфер обмена');
  copyIcon.value = successCopyIcon;
  setTimeout(() => {
    copyIcon.value = defaultCopyIcon;
  }, 1000);
};
const showResendButton = computed(() => props.message.type === MESSAGE_TYPE_USER);

const handleOriginalCopy = async () => {
  await copyMessage(props.message.text);
  toastStore.showSecondary('Оригинальный ответ скопирован в буфер обмена');
};

const handleRepeatSend = async (messageId) => {
  appStore.startBlockSending();
  await chatStore.resendUserMessage(messageId);
};

const popover = ref();

const toggle = (event) => {
  popover.value.toggle(event);
};

const parsedText = computed(() => {
  if (props.message.uml && props.message.uml.length) return '';

  return prepareAiMessage(props.message.text);
});

const showCopyOriginalMessage = computed(() => {
  if (props.message.uml && props.message.uml.length || props.message.type === MESSAGE_TYPE_USER) return false;

  if (!parsedText.value.trim()) return false;
  return true;
});

const showPopover = computed(() => showCopyOriginalMessage.value || appStore.canDeleteMessage || showResendButton.value);

const userText = computed(() => {
  if (props.message.type === MESSAGE_TYPE_USER) {
    return props.message.text;
  }

  return '';
});

const visibleEditModal = ref(false);
const regenerateTooltip = computed(() => appStore.blockSending ? 'Дождитесь завершения предыдущего запроса' : 'Перегенерировать');

const images = ref([]);
onMounted(() => {
  images.value = props.message.uml && props.message.uml.length
      ? getUml(props.message.uml)
      : getImages(props.message.images);
  Prism.highlightAll()
});

const confirm = useConfirm();

const confirmDelete = () => {
  confirm.require({
    message: 'Подтвердите удаление. Операция необратима. Сообщение будет удалено из контекста диалога.',
    header: 'Удаление сообщения',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    rejectProps: {
      label: 'Cancel',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Delete',
      severity: 'danger'
    },
    accept: async () => {
      await useChatStore().removeMessage(props.message.id);
      toastStore.showSecondary('Сообщение удалено.');
    },
  });
};

const confirmRegenerate = (assistantMessageId) => {
  confirm.require({
    message: 'Подтвердите перегенерацию ответа.',
    header: 'Перегенерация ответа',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Отмена',
    rejectProps: {
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Перегенерировать',
      severity: 'info'
    },
    accept: async () => {
      await appStore.handleRegenerateMessageSending(assistantMessageId)
    },
  });
};
</script>